import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Checkbox, Flex } from "@mantine/core";
import { IconCopy } from "@tabler/icons-react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Campaign } from "src/graphql/generated/graphql";
import { RoutePath } from "src/routes";
import { RoutePathParams } from "src/utils/buildUrl";

import { UseCampaignsReturn } from "../../hooks/campaigns";
import { ContextMenuItem } from "../ContextMenu";
import { createColumnHelper, DataGrid } from "../DataGrid";
import { ActionLink } from "../DataGrid/TableRow/TableRow";

const columnHelper = createColumnHelper<Campaign>();

const campaignStateStyles = {
    campaign_state_all: "gray",
    campaign_state_draft: "gray",
    campaign_state_opened: "blue",
    campaign_state_inprogress: "cyan",
    campaign_state_closed: "green",
};

export interface CampaignsDataGridProps extends UseCampaignsReturn {
    enableSelection?: boolean;
    onClick?: (campaign: Campaign) => void;
    getRowHref?: (campaign: Campaign) => RoutePathParams<RoutePath>;
    getContextMenuItems?(campaign: Campaign): ContextMenuItem[];
    onSelectedCampaignsChange?(campaigns: Campaign[]);
}

export const CampaignsDataGrid = ({
    campaigns,
    isLoadingCampaigns,
    onClick,
    onSelectedCampaignsChange,
    getRowHref,
}: CampaignsDataGridProps) => {
    const { t } = useTranslation(["common"]);

    const enableSelection = typeof onSelectedCampaignsChange === "function";
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const columns = useMemo(() => {
        const columns: ColumnDef<Campaign>[] = [
            columnHelper.accessor(
                (row) => ({
                    name: row.name,
                    image: row?.thumbnailImage?.src,
                }),
                {
                    id: "name",
                    header: t("campaign_name"),
                    cell: (props) => (
                        <Flex gap="sm" align="center">
                            {props.getValue()?.image ? (
                                <Avatar src={props.getValue().image} radius="md" size="md" />
                            ) : (
                                <Avatar radius="md" size="md">
                                    {String(props.getValue()?.name)
                                        .split(" ")
                                        .map((w) => w.toUpperCase()[0])
                                        .join("")}
                                </Avatar>
                            )}
                            {props.getValue().name}
                        </Flex>
                    ),
                    footer: (props) => props.column.id,
                }
            ),
            columnHelper.accessor("settings.capacity", {
                id: "settings.capacity",
                header: t("campaign_capacity"),
                cell: (props) => t("campaign_capacity_count", { count: props.getValue() }),
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("settings.openAt", {
                id: "settings.openAt",
                header: t("campaign_settings_openat"),
                cell: (props) => (props?.getValue() ? dayjs(props.getValue()).format("DD.MM.YYYY") : "-"),
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("settings.closeAt", {
                id: "settings.closeAt",
                header: t("campaign_settings_closeat"),
                cell: (props) => (props?.getValue() ? dayjs(props.getValue()).format("DD.MM.YYYY") : "-"),
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("state", {
                id: "state",
                header: t("campaign_state"),
                cell: (props) =>
                    props?.getValue() ? (
                        <Badge color={campaignStateStyles[`campaign_state_${props?.getValue()}`]} size="lg">
                            {t(`campaign_state_${props?.getValue()}`)}
                        </Badge>
                    ) : (
                        "-"
                    ),
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("displayTenants", {
                id: "displayTenants",
                header: t("campaign_display_tenants"),
                cell: (props) =>
                    props?.getValue() ? (
                        <Flex>
                            {props.getValue().map((tenant) => (
                                <Badge key={tenant.id}>{tenant.id}</Badge>
                            ))}
                        </Flex>
                    ) : (
                        "-"
                    ),
                footer: (props) => props.column.id,
            }),
        ];
        if (enableSelection) {
            columns.unshift(
                columnHelper.display({
                    id: "_rowselection",
                    footer: (props) => props.column.id,
                    size: 40,
                    header: ({ table }) => (
                        <Checkbox
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                        />
                    ),
                    cell: ({ row }) => (
                        <Flex display="inline-flex" align="center">
                            <Checkbox
                                mr="xs"
                                checked={row.getIsSelected()}
                                disabled={!row.getCanSelect()}
                                indeterminate={row.getIsSomeSelected()}
                                onChange={() => {}}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    row.getToggleSelectedHandler()(e);
                                }}
                            />
                        </Flex>
                    ),
                })
            );
        }
        return columns;
    }, [t, enableSelection]);

    const campaignsUpdateKey = campaigns.map((campaign) => `${campaign?.id}${campaign?.state}`).join("");

    useEffect(() => {
        const selectedCampaigns = campaigns.filter((campaign) => {
            return rowSelection?.[campaign?.id];
        });

        onSelectedCampaignsChange?.(selectedCampaigns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableSelection, campaignsUpdateKey, JSON.stringify(rowSelection), onSelectedCampaignsChange]);

    const actions: ActionLink<Campaign>[] = useMemo(() => {
        return [
            {
                type: "link",
                icon: <IconCopy size="1.15rem" />,
                label: t("copy"),
                getRoutePathParams: (row) => {
                    const routePathParams: RoutePathParams<RoutePath> = {
                        path: RoutePath.CAMPAIGNS_INQUIRY_CREATE_DUPLICATE_FROM_CAMPAIGN,
                        params: {
                            campaignId: row.id,
                        },
                    };

                    return routePathParams;
                },
            },
        ];
    }, [t]);

    return (
        <DataGrid
            isLoading={isLoadingCampaigns}
            columns={columns}
            data={campaigns}
            onRowClick={onClick}
            getRowHref={getRowHref}
            getRowId={(campaign) => campaign?.id}
            tableLayout="auto"
            mobileHeaderIndex={0}
            secMobileHeaderIndex={2}
            enableRowSelection={enableSelection}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            actions={actions}
        />
    );
};
